<script setup>
import { computed, onMounted, ref, watch } from "vue"
import Swal from "sweetalert2"
import api from "@/api/axios.js"
import { useAuth } from "@/composables/useAuth"

const { user, hasRole } = useAuth()

const DIAS_SEMANA = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"]

const esAdminOSupervisor = computed(() => hasRole("admin") || hasRole("supervisor") || hasRole("rrhh"))
const asesoresLideres = ref([])
const asesorSeleccionado = ref("")
const guardando = ref(false)
const ciudades = ref([])
const ciudadSeleccionada = ref("")
const guardandoCiudad = ref(false)

// bloquesPorDia[diaSemana] = [{ horaInicio, horaFin }]
const bloquesPorDia = ref(DIAS_SEMANA.map(() => []))

async function cargarAsesores() {
  if (!esAdminOSupervisor.value) return
  try {
    const [rAsesores, rCiudades] = await Promise.all([
      api.get("/citas/asesores-lideres"),
      api.get("/catalogos/ciudadAtencion")
    ])
    asesoresLideres.value = rAsesores.data.asesoresLideres
    ciudades.value = rCiudades.data.items
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar la lista de asesores líderes",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

async function guardarCiudad() {
  const id = asesorSeleccionado.value
  if (!id) return

  guardandoCiudad.value = true
  try {
    await api.put(`/citas/asesores-lideres/${id}/ciudad`, { ciudadAtencionId: ciudadSeleccionada.value || null })
    const asesor = asesoresLideres.value.find((a) => a.id === Number(id))
    if (asesor) {
      const ciudad = ciudades.value.find((c) => c.id === Number(ciudadSeleccionada.value))
      asesor.ciudadAtencionId = ciudadSeleccionada.value || null
      asesor.ciudadNombre = ciudad?.nombre || null
    }
    Swal.fire({ icon: "success", title: "Ciudad guardada", timer: 1500, showConfirmButton: false })
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo guardar la ciudad",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    guardandoCiudad.value = false
  }
}

async function cargarHorario() {
  const id = asesorSeleccionado.value
  if (!id) return

  try {
    const { data } = await api.get(`/citas/horario/${id}`)
    const nuevo = DIAS_SEMANA.map(() => [])
    for (const b of data.bloques) {
      nuevo[b.diaSemana].push({ horaInicio: b.horaInicio.slice(0, 5), horaFin: b.horaFin.slice(0, 5) })
    }
    bloquesPorDia.value = nuevo
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el horario",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

onMounted(async () => {
  await cargarAsesores()
  asesorSeleccionado.value = esAdminOSupervisor.value ? "" : user.value?.id || ""
  if (asesorSeleccionado.value) await cargarHorario()
})

watch(asesorSeleccionado, (id) => {
  if (!id) return
  cargarHorario()
  const asesor = asesoresLideres.value.find((a) => a.id === Number(id))
  ciudadSeleccionada.value = asesor?.ciudadAtencionId || ""
})

function agregarBloque(dia) {
  bloquesPorDia.value[dia].push({ horaInicio: "08:00", horaFin: "17:00" })
}

function quitarBloque(dia, idx) {
  bloquesPorDia.value[dia].splice(idx, 1)
}

async function guardar() {
  if (!asesorSeleccionado.value) return

  const bloques = []
  bloquesPorDia.value.forEach((lista, diaSemana) => {
    lista.forEach((b) => bloques.push({ diaSemana, horaInicio: b.horaInicio, horaFin: b.horaFin }))
  })

  guardando.value = true
  try {
    await api.put(`/citas/horario/${asesorSeleccionado.value}`, { bloques })
    Swal.fire({ icon: "success", title: "Horario guardado", timer: 1500, showConfirmButton: false })
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo guardar el horario",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    guardando.value = false
  }
}
</script>

<template>
  <div class="container-fluid py-4">
    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5 class="mb-0"><i class="ni ni-time-alarm text-primary me-2"></i>Horario de Disponibilidad</h5>
        <div v-if="esAdminOSupervisor" style="min-width: 260px;">
          <select class="form-select form-select-sm" v-model="asesorSeleccionado">
            <option value="">Selecciona un asesor líder...</option>
            <option v-for="a in asesoresLideres" :key="a.id" :value="a.id">{{ a.name }}</option>
          </select>
        </div>
      </div>

      <div class="card-body" v-if="asesorSeleccionado">
        <div v-if="esAdminOSupervisor" class="d-flex align-items-end flex-wrap gap-2 mb-4 pb-3 border-bottom">
          <div style="min-width: 260px;">
            <label class="form-label">Ciudad del líder</label>
            <select class="form-select form-select-sm" v-model="ciudadSeleccionada">
              <option value="">Sin especificar</option>
              <option v-for="c in ciudades" :key="c.id" :value="c.id">{{ c.nombre }}</option>
            </select>
          </div>
          <button type="button" class="btn btn-sm btn-outline-primary mb-0" :disabled="guardandoCiudad" @click="guardarCiudad">
            {{ guardandoCiudad ? "Guardando..." : "Guardar ciudad" }}
          </button>
        </div>

        <div v-for="(dia, idx) in DIAS_SEMANA" :key="idx" class="mb-3 pb-3 border-bottom">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">{{ dia }}</h6>
            <button type="button" class="btn btn-sm btn-outline-primary" @click="agregarBloque(idx)">
              <i class="ni ni-fat-add me-1"></i> Agregar bloque
            </button>
          </div>

          <div v-if="bloquesPorDia[idx].length === 0" class="text-muted text-sm">Sin disponibilidad este día.</div>

          <div v-for="(b, bIdx) in bloquesPorDia[idx]" :key="bIdx" class="d-flex align-items-center flex-wrap gap-2 mb-2">
            <input type="time" class="form-control form-control-sm" style="max-width: 140px;" v-model="b.horaInicio">
            <span>-</span>
            <input type="time" class="form-control form-control-sm" style="max-width: 140px;" v-model="b.horaFin">
            <button type="button" class="btn btn-sm btn-outline-danger" @click="quitarBloque(idx, bIdx)">
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>

        <div class="d-flex justify-content-end">
          <button type="button" class="btn btn-primary mb-0" :disabled="guardando" @click="guardar">
            {{ guardando ? "Guardando..." : "Guardar horario" }}
          </button>
        </div>
      </div>

      <div class="card-body" v-else>
        <p class="text-muted mb-0">Selecciona un asesor líder para configurar su horario.</p>
      </div>
    </div>
  </div>
</template>
